/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
/* eslint-enable no-restricted-imports */
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { useDisclosure } from "../../hooks/useDisclosure";

const StreamingAnnouncementSchema = z.object({
  createdAt: z.custom<firebase.firestore.Timestamp>(
    (val) => val instanceof firebase.firestore.Timestamp
  ),
  eventId: z.string(),
  msg: z.string(),
  userId: z.string(),
});
type StreamingAnnouncement = z.infer<typeof StreamingAnnouncementSchema>;

const announcementConverter: firebase.firestore.FirestoreDataConverter<StreamingAnnouncement> =
  {
    fromFirestore(
      snapshot: firebase.firestore.QueryDocumentSnapshot,
      options: firebase.firestore.SnapshotOptions
    ): StreamingAnnouncement {
      const data = snapshot.data(options);
      try {
        return StreamingAnnouncementSchema.parse(data);
      } catch (error) {
        console.error(
          "Zod validation failed for streaming announcement:",
          error
        );
        throw error;
      }
    },
    toFirestore(
      modelObject: StreamingAnnouncement
    ): firebase.firestore.DocumentData {
      return modelObject;
    },
  };

export const useAnnouncement = (mainVideoId: string | undefined) => {
  const [isEditing, setIsEditing] = useState(false);
  const [streamingAnnouncement, setStreamingAnnouncement] =
    useState<string>("");
  const waitingLocalClearChange = useRef<boolean>(false);

  const {
    isOpen,
    onOpen: handleOpenAnnouncement,
    onClose: handleCloseAnnouncement,
    onToggle: handleToggleAnnouncement,
  } = useDisclosure();

  const onStreamingAnnouncementLocalDeleted = useCallback(() => {
    waitingLocalClearChange.current = true;
  }, []);

  useEffect(() => {
    if (mainVideoId === undefined) {
      return;
    }

    // 動画が切り替わった際には、前の動画でのローカル削除待ちフラグをクリアする
    waitingLocalClearChange.current = false;

    const msgRef = `/streamingAnnouncement/${mainVideoId}/history`;

    const typedCollectionRef = firebase
      .firestore()
      .collection(msgRef)
      .withConverter(announcementConverter);

    const unsubscribe = typedCollectionRef
      .orderBy("createdAt", "desc")
      .limit(1)
      .onSnapshot(
        { includeMetadataChanges: true },
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const data = change.doc.data();
              const { msg } = data;

              if (msg.length > 0) {
                // 非空メッセージの更新は waitingLocalClearChange に関わらず必ず反映する
                waitingLocalClearChange.current = false;
                setStreamingAnnouncement(msg);
                handleOpenAnnouncement();
              } else {
                // 空文字がセットされた＝アナウンスが未設定or削除された
                setStreamingAnnouncement("");

                // 操作しているタブ上でのクリア時は自動でタブ閉じないようにする
                if (waitingLocalClearChange.current) {
                  waitingLocalClearChange.current = false;
                  // タブは閉じずにフラグだけクリアする
                } else {
                  handleCloseAnnouncement();
                }
              }
            }
          });
        },
        console.error
      );

    return unsubscribe;
  }, [
    mainVideoId,
    setStreamingAnnouncement,
    handleOpenAnnouncement,
    handleCloseAnnouncement,
  ]);

  return {
    isOpen,
    isEditing,
    streamingAnnouncement,
    handleOpenAnnouncement,
    handleCloseAnnouncement,
    handleToggleAnnouncement,
    setIsEditing,
    setStreamingAnnouncement,
    onStreamingAnnouncementLocalDeleted,
  };
};
